import { getParentNodeWidth, getTableContainerWidth, layoutToWidth } from '@atlaskit/editor-common/node-width';
import { calcTableWidth } from '@atlaskit/editor-common/styles';
import { getBreakpoint, mapBreakpointToLayoutMaxWidth } from '@atlaskit/editor-common/ui';
import { calcTableColumnWidths, containsClassName } from '@atlaskit/editor-common/utils';
import { akEditorFullWidthLayoutWidth, akEditorGutterPaddingDynamic, akEditorTableNumberColumnWidth } from '@atlaskit/editor-shared-styles';
import { hasTableBeenResized } from './colgroup';
import { MAX_SCALING_PERCENT, MAX_SCALING_PERCENT_TABLES_WITH_FIXED_COLUMN_WIDTHS_OPTION } from './consts';

// Translates named layouts in number values.
export function getLayoutSize(tableLayout) {
  var containerWidth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var options = arguments.length > 2 ? arguments[2] : undefined;
  var isFullWidthModeEnabled = options.isFullWidthModeEnabled;
  if (isFullWidthModeEnabled) {
    return containerWidth ? Math.min(containerWidth - akEditorGutterPaddingDynamic() * 2, akEditorFullWidthLayoutWidth) : akEditorFullWidthLayoutWidth;
  }
  var calculatedTableWidth = calcTableWidth(tableLayout, containerWidth, true);
  if (calculatedTableWidth !== 'inherit') {
    return calculatedTableWidth;
  }
  return layoutToWidth[tableLayout] || containerWidth;
}
export function getDefaultLayoutMaxWidth(containerWidth) {
  return mapBreakpointToLayoutMaxWidth(getBreakpoint(containerWidth));
}

// Does the current position point at a cell.
export function pointsAtCell($pos) {
  return $pos.parent.type.spec.tableRole === 'row' && $pos.nodeAfter;
}

// Get the current col width, handles colspan.
export function currentColWidth(view, cellPos, _ref) {
  var colspan = _ref.colspan,
    colwidth = _ref.colwidth;
  var width = colwidth && colwidth[colwidth.length - 1];
  if (width) {
    return width;
  }
  // Not fixed, read current width from DOM
  var domWidth = view.domAtPos(cellPos + 1).node.offsetWidth;
  var parts = colspan || 0;
  if (colwidth) {
    for (var i = 0; i < (colspan || 0); i++) {
      if (colwidth[i]) {
        domWidth -= colwidth[i];
        parts--;
      }
    }
  }
  return domWidth / parts;
}

// Attempts to find a parent TD/TH depending on target element.
export function domCellAround(target) {
  while (target && target.nodeName !== 'TD' && target.nodeName !== 'TH') {
    target = containsClassName(target, 'ProseMirror') ? null : target.parentNode;
  }
  return target;
}
export var getTableMaxWidth = function getTableMaxWidth(_ref2) {
  var table = _ref2.table,
    tableStart = _ref2.tableStart,
    state = _ref2.state,
    layout = _ref2.layout,
    getEditorContainerWidth = _ref2.getEditorContainerWidth;
  var containerWidth = getEditorContainerWidth();
  var parentWidth = getParentNodeWidth(tableStart, state, containerWidth);
  var maxWidth = parentWidth || table.attrs.width || getLayoutSize(layout, containerWidth.width, {});
  if (table.attrs.isNumberColumnEnabled) {
    maxWidth -= akEditorTableNumberColumnWidth;
  }
  return maxWidth;
};

/**
 *
 * @param table
 * @returns calculated width of <table /> element derived from sum of colwidths on tableCell or tableHeader nodes or falls back to container width
 */
export var getTableElementWidth = function getTableElementWidth(table) {
  if (hasTableBeenResized(table)) {
    // TODO: is there a scenario where ADF columns are SMALLER than container width?
    return calcTableColumnWidths(table).reduce(function (sum, width) {
      return sum + width;
    }, 0);
  }
  return getTableContainerElementWidth(table);
};
export var getTableContainerElementWidth = function getTableContainerElementWidth(table) {
  return getTableContainerWidth(table);
};
export var getTableScalingPercent = function getTableScalingPercent(table, tableRef, shouldUseIncreasedScalingPercent) {
  var _tableRef$parentEleme;
  var maxScalingPercent = shouldUseIncreasedScalingPercent ? MAX_SCALING_PERCENT_TABLES_WITH_FIXED_COLUMN_WIDTHS_OPTION : MAX_SCALING_PERCENT;
  var tableWidth = getTableContainerElementWidth(table);
  var renderWidth = (tableRef === null || tableRef === void 0 || (_tableRef$parentEleme = tableRef.parentElement) === null || _tableRef$parentEleme === void 0 ? void 0 : _tableRef$parentEleme.clientWidth) || tableWidth;
  // minus 1 here to avoid any 1px scroll in Firefox
  var scalePercent = (renderWidth - 1) / tableWidth;
  scalePercent = Math.max(scalePercent, 1 - maxScalingPercent);
  return Math.min(scalePercent, 1);
};
export var getStaticTableScalingPercent = function getStaticTableScalingPercent(table, tableRenderWidth, shouldUseIncreasedScalingPercent) {
  var maxScalingPercent = shouldUseIncreasedScalingPercent ? MAX_SCALING_PERCENT_TABLES_WITH_FIXED_COLUMN_WIDTHS_OPTION : MAX_SCALING_PERCENT;
  var tableWidth = getTableContainerElementWidth(table);
  // minus 1 here to avoid any 1px scroll in Firefox
  var scalePercent = (tableRenderWidth - 1) / tableWidth;
  scalePercent = Math.max(scalePercent, 1 - maxScalingPercent);
  return Math.min(scalePercent, 1);
};